import { useEffect, useState } from 'react';

import { I18n } from 'common/i18n';
import StorytellerUtils from 'lib/StorytellerUtils';
import { type ReactRendererProps } from '../shared/ComponentRegistry';

interface StoryTileValue {
  domain: string;
  storyUid: string;
  openInNewWindow?: boolean;
}

interface StoryTileData {
  description: string | null;
  /** URL of the image */
  image: string | null;
  title: string;
  url: string;
}

/**
 * Sample componentData:
 *  {
 *    type: "story.tile",
 *    value: {
 *      domain: <story domain>,
 *      storyUid: <story four-by-four>
 *    }
 *  }
 */
export const ComponentStoryTile = ({ componentData }: ReactRendererProps) => {
  const value = componentData.value as StoryTileValue;
  const typeClasses = StorytellerUtils.typeToClassesForComponentType(componentData.type);
  const storyTileSrc = StorytellerUtils.generateStoryTileJsonSrc(value.domain, value.storyUid);

  const [storyTileData, setStoryTileData] = useState<StoryTileData | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Ignore responses that resolve after the src has changed or the component has unmounted
    let ignore = false;

    setStoryTileData(null);
    setHasError(false);

    fetch(storyTileSrc)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data: StoryTileData) => {
        if (!ignore) {
          setStoryTileData(data);
        }
      })
      .catch((error) => {
        if (!ignore) {
          console.error(error);
          setHasError(true);
        }
      });

    return () => {
      ignore = true;
    };
  }, [storyTileSrc]);

  if (hasError) {
    return (
      <div className={`component ${typeClasses} component-error`}>
        <p>{I18n.t('editor.story_tile.invalid_permissions')}</p>
      </div>
    );
  }

  if (!storyTileData) {
    return <div className={`component ${typeClasses}`} />;
  }

  const viewStoryLabel = `${I18n.t('editor.story_tile.view_story_label')}${storyTileData.title}`;

  return (
    <div className={`component ${typeClasses} rendered`}>
      <a
        className="story-tile-container"
        href={storyTileData.url}
        target={value.openInNewWindow ? '_blank' : '_self'}
        rel={value.openInNewWindow ? 'noopener noreferrer' : undefined}
        aria-label={viewStoryLabel}
        data-testid="component-story-tile-link"
      >
        <div className="story-tile typeset">
          <div className="story-tile-title-container">
            <h2 className="story-tile-title">{storyTileData.title}</h2>
          </div>
          <div
            className="story-tile-image"
            style={storyTileData.image ? { backgroundImage: `url(${storyTileData.image})` } : undefined}
          />
          <div className="story-tile-description">{storyTileData.description}</div>
          <div className="story-tile-view-story forge-button forge-button--text">{viewStoryLabel}</div>
        </div>
      </a>
    </div>
  );
};
